import * as React from 'react'
import styled from 'styled-components'
import { Box, Button, get } from '@ally/metronome-ui'
import { formatCurrency } from '@ally/aos-utils'
import { GroupedAccounts } from '../../../types'
import { getDashDash } from '../../../utils'

interface BalanceChartTitleProps {
  accountGroup: string
  groupedAccounts: GroupedAccounts
  setSelected: (accountGroup: string) => void
  hasStaleData?: boolean
}

const StyledTitle = styled(Box).attrs(() => ({
  'data-testid': 'chart-title',
}))`
  position: absolute;
  width: fit-content;
  text-align: center;
  transform: translateY(-50%);
  top: 50%;
  color: ${get.colors('slate-5')};
  font-size: ${get.fontSizes(1)};
  line-height: 21px;
  z-index: 1;
`

const numberOfAccountsLabel = (numAccounts: number): string => {
  return `${numAccounts} account${numAccounts > 1 ? 's' : ''}`
}

const staleDataCopy = getDashDash(
  'We can’t update your account information right now, but we’re working on it.',
)

const BalanceChartTitle: React.FC<BalanceChartTitleProps> = ({
  accountGroup,
  groupedAccounts,
  setSelected,
  hasStaleData = false,
}): React.ReactElement => {
  function resetChart(): void {
    setSelected('')
  }

  return (
    <StyledTitle>
      {accountGroup === 'INVW' && (
        <>
          <Box fontWeight="bold">Wealth Management</Box>
          <Box>
            {numberOfAccountsLabel(groupedAccounts.INVW.numberOfAccounts)}
          </Box>
        </>
      )}
      {accountGroup === 'INVA' && (
        <>
          <Box fontWeight="bold">Robo Portfolio</Box>
          <Box>
            {numberOfAccountsLabel(groupedAccounts.INVA.numberOfAccounts)}
          </Box>
          <Box fontWeight="bold">Total Securities</Box>
          <Box>
            {hasStaleData
              ? staleDataCopy
              : formatCurrency.asCurrency(
                  groupedAccounts.INVA.aggMarketValueTotal,
                )}
          </Box>
          <Box fontWeight="bold">Total Cash Value</Box>
          <Box>
            {hasStaleData
              ? staleDataCopy
              : formatCurrency.asCurrency(
                  groupedAccounts.INVA.aggCashMoneyMarketTotal,
                )}
          </Box>
        </>
      )}
      {accountGroup === 'INVS' && (
        <>
          <Box fontWeight="bold">Self-Directed</Box>
          <Box>
            {numberOfAccountsLabel(groupedAccounts.INVS.numberOfAccounts)}
          </Box>
          <Box fontWeight="bold">Total Securities</Box>
          <Box>
            {hasStaleData
              ? staleDataCopy
              : formatCurrency.asCurrency(
                  groupedAccounts.INVS.aggMarketValueTotal,
                )}
          </Box>
          <Box fontWeight="bold">Total Cash Value</Box>
          <Box>
            {hasStaleData
              ? staleDataCopy
              : formatCurrency.asCurrency(
                  groupedAccounts.INVS.aggCashMoneyMarketTotal,
                )}
          </Box>
        </>
      )}
      {accountGroup === 'CHECKING' && (
        <>
          <Box fontWeight="bold">Checking</Box>
          <Box>
            {numberOfAccountsLabel(groupedAccounts.CHECKING.numberOfAccounts)}
          </Box>
          <Box fontWeight="bold">Total Avail Bal</Box>
          <Box>
            {formatCurrency.asCurrency(groupedAccounts.CHECKING.aggBalance)}
          </Box>
          <Box fontWeight="bold">Total Int YTD</Box>
          <Box>
            {formatCurrency.asCurrency(groupedAccounts.CHECKING.aggInterestYtd)}
          </Box>
        </>
      )}
      {accountGroup === 'SAVINGS' && (
        <>
          <Box fontWeight="bold">Savings</Box>
          <Box>
            {numberOfAccountsLabel(groupedAccounts.SAVINGS.numberOfAccounts)}
          </Box>
          <Box fontWeight="bold">Total Avail Bal</Box>
          <Box>
            {formatCurrency.asCurrency(groupedAccounts.SAVINGS.aggBalance)}
          </Box>
          <Box fontWeight="bold">Total Int YTD</Box>
          <Box>
            {formatCurrency.asCurrency(groupedAccounts.SAVINGS.aggInterestYtd)}
          </Box>
        </>
      )}
      {accountGroup === 'CD' && (
        <>
          <Box fontWeight="bold">CD&#39;s</Box>
          <Box>
            {numberOfAccountsLabel(groupedAccounts.CD.numberOfAccounts)}
          </Box>
          <Box fontWeight="bold">Total Available</Box>
          <Box>{formatCurrency.asCurrency(groupedAccounts.CD.aggBalance)}</Box>
          <Box fontWeight="bold">Interest Accrued</Box>
          <Box>
            {formatCurrency.asCurrency(groupedAccounts.CD.aggInterestAccrued)}
          </Box>
        </>
      )}
      {accountGroup === 'IRA' && (
        <>
          <Box fontWeight="bold">IRA&#39;s</Box>
          <Box>
            {numberOfAccountsLabel(groupedAccounts.IRA.numberOfAccounts)}
          </Box>
          {groupedAccounts.IRA.hasSingleIraType && (
            <>
              <Box fontWeight="bold">Total Available</Box>
              <Box>
                {formatCurrency.asCurrency(groupedAccounts.IRA.aggBalance)}
              </Box>
              <Box fontWeight="bold">Interest Accrued</Box>
              <Box>
                {formatCurrency.asCurrency(groupedAccounts.IRA.aggInterestYtd)}
              </Box>
            </>
          )}
        </>
      )}
      {accountGroup !== '' && (
        <Button variant="link" text="Go back" onClick={resetChart} />
      )}
    </StyledTitle>
  )
}

export default BalanceChartTitle
